import { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ListingCard } from '@/components/marketplace/ListingCard';
import { ListingDetailModal } from '@/components/marketplace/ListingDetailModal';
import { CreateListingModal } from '@/components/marketplace/CreateListingModal';
import { Plus, Search, FolderOpen } from 'lucide-react';

// Mock data - will be replaced with real data from database
const mockListings = [
  {
    id: '1',
    portfolio_name: 'Consumer Credit Portfolio Q4 2024',
    seller_org: 'ABC Credit Solutions',
    asking_price: 4500000,
    account_count: 1250,
    nominal_value: 15000000,
    irr_target: 18.5,
    listed_at: '2025-01-15',
    status: 'active' as const,
    description: 'High-quality consumer credit portfolio with strong recovery potential. Average account age 2.3 years.',
  },
  {
    id: '2',
    portfolio_name: 'Vehicle Finance Book',
    seller_org: 'XYZ Finance',
    asking_price: 8200000,
    account_count: 890,
    nominal_value: 28000000,
    irr_target: 22.0,
    listed_at: '2025-01-10',
    status: 'active' as const,
    description: 'Secured vehicle finance portfolio. All accounts have valid collateral.',
  },
];

const mockMyListings = [];
const mockPortfolios = [];

const Listings = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const filteredListings = mockListings.filter(
    (listing) =>
      listing.portfolio_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      listing.seller_org.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Marketplace</h1>
            <p className="text-muted-foreground">
              Browse portfolios for sale and list your own
            </p>
          </div>
          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Listing
          </Button>
        </div>

        <Tabs defaultValue="browse" className="space-y-6">
          <TabsList>
            <TabsTrigger value="browse">Browse Listings</TabsTrigger>
            <TabsTrigger value="my-listings">My Listings</TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search portfolios..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {filteredListings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="rounded-full bg-muted p-6 mb-4">
                  <FolderOpen className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">No listings found</h3>
                <p className="text-muted-foreground max-w-md">
                  {searchQuery
                    ? 'Try adjusting your search query'
                    : 'No portfolios are currently listed on the marketplace'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredListings.map((listing) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    onViewDetails={() => setSelectedListing(listing)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="my-listings" className="space-y-4">
            {mockMyListings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="rounded-full bg-muted p-6 mb-4">
                  <FolderOpen className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">No active listings</h3>
                <p className="text-muted-foreground max-w-md mb-4">
                  You haven't created any listings yet. Start by listing a portfolio.
                </p>
                <Button onClick={() => setCreateModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Listing
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* My listings will appear here */}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <ListingDetailModal
        listing={selectedListing}
        open={!!selectedListing}
        onClose={() => setSelectedListing(null)}
      />

      <CreateListingModal
        portfolios={mockPortfolios}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => {
          // Refresh listings
        }}
      />
    </AppLayout>
  );
};

export default Listings;
